import type { AiPlanBody, AiPlanGap, AiPlanStep, AiPlanView, AiRefInfo } from 'services/aiService'
import type { Capabilities } from 'utils/capabilities'

/**
 * The plan card's reasoning, kept out of the components so it is tested on its
 * own (MYD-623): what a step says, what depends on what, and whether the plan
 * may run.
 */

export const STEP_REF = /^\$(s\d{1,2})\.id$/

/** The step a `$sN.id` value names, or null. */
export const stepRefTarget = (value: unknown): string | null => (typeof value === 'string' ? (value.match(STEP_REF)?.[1] ?? null) : null)

/** Every string in a value, however deep. */
const stringsIn = (value: unknown, out: string[] = []): string[] => {
	if (typeof value === 'string') out.push(value)
	else if (Array.isArray(value)) value.forEach(item => stringsIn(item, out))
	else if (value && typeof value === 'object') Object.values(value).forEach(item => stringsIn(item, out))
	return out
}

/** The steps this one needs: its `dependsOn`, and any step its args name as `$sN.id`. */
export const needsOf = (step: AiPlanStep): string[] =>
	[...new Set([...(step.dependsOn ?? []), ...stringsIn(step.args).map(stepRefTarget).filter((id): id is string => Boolean(id))])]

/**
 * Removing a step removes everything that needs it, all the way down: a
 * project that sits on a location, and the partner added to that project.
 * Returns the ids in plan order, the step itself first.
 */
export const removalOf = (plan: AiPlanBody, stepId: string): string[] => {
	const doomed = new Set([stepId])
	let grew = true
	while (grew) {
		grew = false
		for (const step of plan.steps) {
			if (!doomed.has(step.id) && needsOf(step).some(id => doomed.has(id))) {
				doomed.add(step.id)
				grew = true
			}
		}
	}
	return plan.steps.filter(step => doomed.has(step.id)).map(step => step.id)
}

export const withoutSteps = (plan: AiPlanBody, ids: string[]): AiPlanBody => ({
	...plan,
	steps: plan.steps.filter(step => !ids.includes(step.id)).map(step => ({ ...step, dependsOn: (step.dependsOn ?? []).filter(id => !ids.includes(id)) })),
})

export const replaceStep = (plan: AiPlanBody, next: AiPlanStep): AiPlanBody => ({
	...plan,
	steps: plan.steps.map(step => (step.id === next.id ? next : step)),
})

// =============================================================================
// Words
// =============================================================================

/** One run of a sentence: plain, or emphasised (a name). */
export type Phrase = { text: string; strong?: boolean }

const plain = (text: string): Phrase => ({ text })
const strong = (text: string): Phrase => ({ text, strong: true })

/** A place's address on one line, "1750 Airline Hwy, Hollister CA". */
export const placeAddress = (place?: AiRefInfo['place']) =>
	place ? [place.address, [place.city, place.stateProvince].filter(Boolean).join(' ')].filter(Boolean).join(', ') : ''

/**
 * What a reference is called: a record's label from `refs`, or for `$sN.id`
 * the name the earlier step gives what it makes.
 */
export const nameOf = (value: unknown, plan: AiPlanBody, refs: Record<string, AiRefInfo> = {}): string => {
	const target = stepRefTarget(value)
	if (target) {
		const step = plan.steps.find(candidate => candidate.id === target)
		if (!step) return 'an earlier step'
		if (step.op === 'location.useExisting') return nameOf(step.args.locationRef, plan, refs)
		return step.args.name ? `the new ${kindWord(step.op)} ${step.args.name}` : `the new ${kindWord(step.op)}`
	}
	if (typeof value !== 'string') return ''
	const info = refs[value]
	if (!info) return 'a record from this conversation'
	// "Safeway · 1 San Felipe Rd, Hollister" names a place; the name alone reads better in a sentence.
	return info.place?.name || info.label.split(' · ')[0] || info.label
}

const kindWord = (op: string) =>
	({
		'location.create': 'location',
		'project.create': 'project',
		'task.create': 'task',
		'evaluation.create': 'evaluation',
		'evaluationSite.addFromLocation': 'site',
		'evaluationSite.addFromPlace': 'site',
		'explorerReport.createFromTemplate': 'report',
		'explorerReport.createFromDefinition': 'report',
		'presentation.createFromTemplate': 'deck',
	})[op] ?? 'record'

const ACCESS_WORDS: Record<string, string> = { full: 'full access', readonly: 'read-only access' }

/** What each row of a report is, in a sentence (MYD-626). */
const GRAIN_WORDS: Record<string, string> = { location: 'locations', project: 'projects', task: 'tasks', evaluationSite: 'sites' }

/** The step as a sentence, e.g. "Create location **Safeway**, 1 San Felipe Rd, Hollister CA". */
export const describeStep = (step: AiPlanStep, plan: AiPlanBody, refs: Record<string, AiRefInfo> = {}): Phrase[] => {
	const { args } = step
	const name = (value: unknown) => nameOf(value, plan, refs)
	switch (step.op) {
		case 'location.create': {
			const address = placeAddress(refs[args.placeRef]?.place)
			return [plain('Create location '), strong(args.name), ...(address ? [plain(`, ${address}`)] : [])]
		}
		case 'location.useExisting':
			return [plain('Use your existing location '), strong(name(args.locationRef))]
		case 'project.create': {
			// "Create project Remodel", not "Create Remodel project Remodel".
			const typed = args.projectType && args.projectType !== args.name ? ` (${args.projectType})` : ''
			return [plain('Create project '), strong(args.name), plain(`${typed} at ${name(args.locationRef)}`)]
		}
		case 'asset.addPartner':
			return [plain('Add '), strong(name(args.partnerRef)), plain(` to ${name(args.asset?.ref)}, with ${ACCESS_WORDS[args.accessLevel] ?? 'full access'}`)]
		case 'partner.inviteByEmail':
			return [
				plain('Invite '),
				strong(args.name),
				plain(args.email ? ` (${args.email})` : ''),
				plain(args.asset ? ` to ${name(args.asset.ref)}` : ' to work with you'),
			]
		case 'task.create':
			return [plain('Create task '), strong(args.name), plain(` on ${name(args.projectRef)}`), ...(args.dueDate ? [plain(`, due ${args.dueDate}`)] : [])]
		case 'task.applyTemplate':
			return [plain('Add the tasks from '), strong(name(args.templateRef)), plain(` to ${name(args.projectRef)}`)]
		case 'evaluation.create':
			return [plain('Create evaluation '), strong(args.name), ...(args.marketArea ? [plain(` for ${args.marketArea}`)] : [])]
		case 'evaluationSite.addFromLocation':
			return [plain('Add '), strong(name(args.locationRef)), plain(` as a site in ${name(args.evaluationRef)}`)]
		case 'evaluationSite.addFromPlace': {
			const place = refs[args.placeRef]?.place
			return [plain('Add '), strong(args.name || place?.name || name(args.placeRef)), plain(` as a site in ${name(args.evaluationRef)}`)]
		}
		case 'explorerReport.createFromTemplate':
			return [plain('Create the report '), strong(args.name || name(args.templateRef)), ...(args.scope ? [plain(` for ${name(args.scope.ref)}`)] : [])]
		case 'explorerReport.createFromDefinition':
			return [
				plain('Create the report '),
				strong(args.name),
				plain(` of ${GRAIN_WORDS[args.grain] ?? 'rows'}, with ${args.columns?.length ?? 0} ${args.columns?.length === 1 ? 'column' : 'columns'}`),
				...(args.scope ? [plain(`, for ${name(args.scope.ref)}`)] : []),
			]
		case 'presentation.createFromTemplate':
			return [plain('Create the deck '), strong(args.name || name(args.templateRef)), plain(` for ${name(args.anchor?.ref)}`)]
		case 'portfolio.addLocation':
			return [plain('Add '), strong(name(args.locationRef)), plain(' to the portfolio '), strong(name(args.portfolioRef))]
		default:
			return [plain(step.label || step.op)]
	}
}

export const sentenceText = (phrases: Phrase[]) => phrases.map(phrase => phrase.text).join('')

// =============================================================================
// Gaps and whether it may run
// =============================================================================

/** What this step is still waiting on. */
export const gapsOf = (stepId: string, view: Pick<AiPlanView, 'blocking'>): AiPlanGap[] => (view.blocking ?? []).filter(gap => gap.step === stepId)

/** Ops that make an asset, which only accounts that may create assets can run. */
const CREATES_ASSET = new Set(['location.create', 'project.create', 'evaluation.create'])

/**
 * Why Confirm is disabled, in words for the button's reason line, or null
 * when it may be pressed. In order of what the user can do about it.
 */
export const confirmBlocker = ({
	view,
	capabilities,
	saving,
}: {
	view: AiPlanView
	capabilities: Pick<Capabilities, 'canWrite' | 'canCreateAssets' | 'isImpersonating'>
	saving: boolean
}): string | null => {
	if (view.status === 'executing') return 'The plan is running.'
	if (!['proposed', 'edited'].includes(view.status)) return 'This plan has already run.'
	if (capabilities.isImpersonating) return 'Support sessions are read-only, so this plan cannot run.'
	if (!capabilities.canWrite) return 'Your account cannot make changes.'
	if (!capabilities.canCreateAssets && view.plan.steps.some(step => CREATES_ASSET.has(step.op))) return 'Your account cannot create new locations, projects or evaluations.'
	if (saving) return 'Saving your changes…'
	if (!view.plan.steps.length) return 'There is nothing left to do.'
	const gaps = view.blocking ?? []
	if (gaps.length) return gaps.length === 1 ? 'One step still needs your input.' : `${gaps.length} steps still need your input.`
	if (view.executable === false) return 'This plan cannot run yet.'
	return null
}

// =============================================================================
// A run (MYD-624)
// =============================================================================

export type StepProgress = 'pending' | 'running' | 'done' | 'failed' | 'skipped'

/**
 * Each step's state, from the results saved so far. While the plan runs, the
 * first step without a result is the one running: the executor runs them in
 * order and saves each result as it lands.
 */
export const progressOf = (view: Pick<AiPlanView, 'plan' | 'results' | 'status'>): Record<string, StepProgress> => {
	const results = new Map(view.results.map(result => [result.stepId, result]))
	let runningFound = false
	return Object.fromEntries(
		view.plan.steps.map(step => {
			const result = results.get(step.id)
			if (result) return [step.id, result.status]
			if (view.status === 'executing' && !runningFound) {
				runningFound = true
				return [step.id, 'running']
			}
			return [step.id, 'pending']
		}),
	)
}

/** Something a run made (or found), with where to open it. */
export interface ResultLink {
	stepId: string
	kind: string
	label: string
	url: string
	reused: boolean
}

const KIND_WORDS: Record<string, string> = {
	location: 'Location',
	project: 'Project',
	evaluation: 'Evaluation',
	evaluationSite: 'Site',
	report: 'Report',
	presentation: 'Deck',
}

export const kindLabel = (kind: string) => KIND_WORDS[kind] ?? 'Record'

/** Links to what the run made, in plan order. Only results the server gave a page for. */
export const resultLinks = (view: Pick<AiPlanView, 'plan' | 'results'>): ResultLink[] => {
	const order = new Map(view.plan.steps.map((step, index) => [step.id, index]))
	return view.results
		.filter(result => result.status === 'done' && result.created?.url && result.created.kind)
		.sort((a, b) => (order.get(a.stepId) ?? 0) - (order.get(b.stepId) ?? 0))
		.map(result => ({
			stepId: result.stepId,
			kind: result.created!.kind as string,
			label: result.created!.label || kindLabel(result.created!.kind as string),
			url: result.created!.url as string,
			reused: Boolean(result.created!.reused),
		}))
}

/** Undo is offered while the window is open. */
export const canUndo = (view: Pick<AiPlanView, 'status' | 'undoUntil'>, now: number = Date.now()) =>
	(view.status === 'done' || view.status === 'partial') && Boolean(view.undoUntil) && new Date(view.undoUntil as string).getTime() > now
